// Combined auth modal: hosts five distinct flows in one file —
//   1. Sign in (email/password) + Google SSO
//   2. Sign up (email/password) + Google SSO + plan picker
//   3. Forgot password (request reset link)
//   4. Reset password (with token from URL ?reset_token=…)
//   5. Email verification (link from registration email at /verify-email)
//
// JSX is divided into clearly-marked sections (search "═══" or "Dialog" to
// jump between them). Brand colors live in src/theme/colors.js; see
// frontend/README.md "Styling" section.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Tabs, Tab, Box, Alert,
  InputAdornment, IconButton, Typography, CircularProgress,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useApp, getEmbedParentUrl } from '../../context/AppContext';
import { planCards } from '../../data/plans';
import { GOOGLE_BUTTON_RENDER_POLL_MS, GOOGLE_BUTTON_RENDER_MAX_ATTEMPTS } from '../../utils/constants';
import { isValidEmail } from '../../utils/validation';
import { trackEvent, TRACKED_EVENTS } from '../../utils/analytics';
import PlanCard from '../PlanCard';

export default function AuthModal() {
  const {
    API_BASE_URL, GOOGLE_CLIENT_ID, GOOGLE_LOGIN_URI,
    setToken, setAuthOk, email, setEmail,
    setSubscriptionStatus, setSubscriptionRequired, setPendingSubscriptionEmail,
    setInfoMessage, authModalOpen, setAuthModalOpen,
    authTab, setAuthTab, pendingCheckoutPlan, setPendingCheckoutPlan,
    planDialogOpen, setPlanDialogOpen,
    handleCheckout, scrollToPricing,
  } = useApp();

  // Local form state
  const [loginPassword, setLoginPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirm, setRegisterConfirm] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Reset password state (token from URL)
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Email verification state
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verifyEmailResult, setVerifyEmailResult] = useState(null);

  // Google
  const [googleReady, setGoogleReady] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const googleButtonRef = useRef(null);

  // ─── URL param effects ──────────────────────────────────────────────────

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    const resetToken = params.get('reset_token');
    if (resetToken) {
      setResetPasswordToken(resetToken);
      setResetPasswordOpen(true);
      params.delete('reset_token');
      window.history.replaceState({}, '', `${window.location.origin}${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search || '');
    const verifyToken = params.get('token');
    if ((pathname === '/verify-email' || pathname.endsWith('/verify-email')) && verifyToken) {
      setVerifyingEmail(true);
      axios.post(`${API_BASE_URL}/auth/verify-email`, { token: verifyToken })
        .then(res => {
          trackEvent(TRACKED_EVENTS.EMAIL_VERIFIED);
          setVerifyEmailResult({ success: true, message: res?.data?.message || 'Email verified successfully!' }); setVerifyingEmail(false);
        })
        .catch(err => { setVerifyEmailResult({ success: false, message: err?.response?.data?.error || 'Verification failed. The link may have expired.' }); setVerifyingEmail(false); });
      window.history.replaceState({}, '', window.location.origin);
    }
  }, [API_BASE_URL]);

  // ─── Google Identity setup ──────────────────────────────────────────────

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const existing = document.getElementById('google-identity-script');
    if (existing) { setGoogleReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true; script.defer = true; script.id = 'google-identity-script';
    script.onload = () => setGoogleReady(true);
    document.head.appendChild(script);
    return () => script.remove();
  }, [GOOGLE_CLIENT_ID]);

  const decodeEmailFromIdToken = (idToken) => {
    try {
      const base64 = (idToken || '').split('.')[1] || '';
      const decoder = window.atob || (typeof atob !== 'undefined' ? atob : null);
      if (!decoder) return '';
      return (JSON.parse(decoder(base64))?.email || '').toLowerCase();
    } catch (_) { return ''; }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setAuthError('');
    setInfoMessage('');
    if (!credential) { setAuthError('Google sign-in failed. Please try again.'); return; }
    const emailFromToken = decodeEmailFromIdToken(credential);
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/google`, { id_token: credential });
      const t = res?.data?.token;
      const subRequired = Boolean(res?.data?.subscription_required);
      const responseEmail = (res?.data?.email || emailFromToken || '').toLowerCase();
      if (t) {
        trackEvent(
          res?.data?.is_new_user ? TRACKED_EVENTS.SIGNUP_GOOGLE_COMPLETED : TRACKED_EVENTS.LOGIN_COMPLETED,
          { method: 'google' }
        );
        setToken(t); setAuthOk(true); setAuthModalOpen(false);
        window.localStorage.setItem('jwt', t);
        if (responseEmail) { window.localStorage.setItem('userEmail', responseEmail); setEmail(responseEmail); }
        setSubscriptionRequired(subRequired);
        setSubscriptionStatus(res?.data?.subscription_status || (subRequired ? 'pending_payment' : 'active'));
        setPendingSubscriptionEmail(subRequired ? responseEmail : '');
        setInfoMessage(res?.data?.message || '');
        if (pendingCheckoutPlan) {
          const plan = pendingCheckoutPlan; setPendingCheckoutPlan(null);
          await handleCheckout(plan, true);
        }
        if (res?.data?.is_new_user) setPlanDialogOpen(true);
        return;
      }
      setAuthError('Google sign-in failed.');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || 'Google sign-in failed.';
      if (status === 402) {
        setSubscriptionRequired(true); setPendingSubscriptionEmail(emailFromToken || email);
        setInfoMessage(msg); setAuthError(''); scrollToPricing();
      } else { setAuthError(msg); }
    }
  }, [API_BASE_URL, email, pendingCheckoutPlan, setPendingCheckoutPlan, handleCheckout, scrollToPricing,
    setToken, setAuthOk, setAuthModalOpen, setEmail, setSubscriptionRequired, setSubscriptionStatus,
    setPendingSubscriptionEmail, setInfoMessage, setPlanDialogOpen]);

  // Handle Google redirect credential in URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    const cred = params.get('credential');
    if (cred) {
      handleGoogleCredential(cred);
      params.delete('credential');
      window.history.replaceState({}, '', `${window.location.origin}${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    }
  }, [handleGoogleCredential]);

  const renderGoogleButton = useCallback(() => {
    try {
      if (!googleButtonRef.current || !window.google?.accounts?.id) { setGoogleAvailable(false); return false; }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID, ux_mode: 'popup', login_uri: GOOGLE_LOGIN_URI,
        callback: (response) => handleGoogleCredential(response?.credential),
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, { theme: 'outline', size: 'large', width: 320 });
      setGoogleAvailable(true); return true;
    } catch (_) { setGoogleAvailable(false); return false; }
  }, [GOOGLE_CLIENT_ID, GOOGLE_LOGIN_URI, handleGoogleCredential]);

  useEffect(() => {
    if (!authModalOpen || !GOOGLE_CLIENT_ID) return;
    if (renderGoogleButton()) return;
    // Bounded polling: give the Google Identity script ~15s to load, then
    // stop instead of polling for as long as the modal stays open.
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (renderGoogleButton() || attempts >= GOOGLE_BUTTON_RENDER_MAX_ATTEMPTS) clearInterval(interval);
    }, GOOGLE_BUTTON_RENDER_POLL_MS);
    return () => clearInterval(interval);
  }, [authModalOpen, GOOGLE_CLIENT_ID, renderGoogleButton, googleReady]);

  // ─── Auth handlers ────────────────────────────────────────────────────────

  const handleLogin = async () => {
    setAuthError(''); setInfoMessage(''); setSubscriptionRequired(false); setFormErrors({});
    const emailValid = isValidEmail(email);
    const errors = {};
    if (!email) errors.email = 'Email is required';
    else if (!emailValid) errors.email = 'Enter a valid email';
    if (!loginPassword) errors.loginPassword = 'Password is required';
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/login`, { email, password: loginPassword });
      const t = res?.data?.token;
      if (!t) { setAuthError('Login did not return a token. Please try again.'); setAuthOk(false); return; }
      trackEvent(TRACKED_EVENTS.LOGIN_COMPLETED, { method: 'password' });
      const subRequired = Boolean(res?.data?.subscription_required);
      setToken(t); setAuthOk(true);
      setInfoMessage(res?.data?.message || '');
      setSubscriptionRequired(subRequired);
      setSubscriptionStatus(res?.data?.subscription_status || (subRequired ? 'pending_payment' : 'active'));
      window.localStorage.setItem('jwt', t); window.localStorage.setItem('userEmail', email);
      setPendingSubscriptionEmail(subRequired ? email : '');
      setAuthModalOpen(false);
      if (pendingCheckoutPlan) {
        const plan = pendingCheckoutPlan; setPendingCheckoutPlan(null);
        await handleCheckout(plan, true);
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      if (status === 403 && data?.verification_required) {
        setVerificationEmail(data?.email || email);
        setVerificationSent(false); setVerificationDialogOpen(true);
        setAuthError(''); setAuthOk(false); setAuthModalOpen(false); return;
      }
      if (status === 402) {
        setSubscriptionRequired(true); setPendingSubscriptionEmail(email);
        setInfoMessage(data?.error || ''); setAuthError(''); scrollToPricing();
      } else {
        setAuthError(data?.error || 'Login failed. Check your email/password or create an account.');
      }
      setAuthOk(false);
    }
  };

  const handleRegister = async () => {
    setAuthError(''); setInfoMessage(''); setSubscriptionRequired(false); setFormErrors({});
    const emailValid = isValidEmail(registerEmail);
    const errors = {};
    if (!registerEmail) errors.registerEmail = 'Email is required';
    else if (!emailValid) errors.registerEmail = 'Enter a valid email';
    if (!registerPassword) errors.registerPassword = 'Password is required';
    if (Object.keys(errors).length) { setFormErrors(errors); setAuthError('Email and password are required.'); return; }
    if (registerPassword !== registerConfirm) { setAuthError('Passwords do not match.'); return; }
    try {
      const trackingInfo = JSON.parse(window.localStorage.getItem('ad_tracking_params') || 'null');
      const res = await axios.post(`${API_BASE_URL}/auth/register`, { email: registerEmail, password: registerPassword, trackingInfo });
      trackEvent(TRACKED_EVENTS.SIGNUP_COMPLETED, { method: 'password' });
      const verificationRequired = res?.data?.verification_required ?? res?.data?.verificationRequired ?? false;
      const verificationEmailSent = res?.data?.verificationEmailSent ?? res?.data?.verification_email_sent ?? true;
      const t = res?.data?.token;
      const subRequired = Boolean(res?.data?.subscription_required);

      setInfoMessage(res?.data?.message || (verificationRequired
        ? 'Account created. Please check your email to verify your account.'
        : 'Account created. Choose a plan to continue.'));
      setSubscriptionRequired(subRequired);
      setSubscriptionStatus(res?.data?.subscription_status || (subRequired ? 'pending_payment' : 'active'));
      setPendingSubscriptionEmail(registerEmail);
      setEmail(registerEmail);

      if (verificationRequired) {
        setVerificationEmail(registerEmail); setVerificationSent(verificationEmailSent);
        setVerificationDialogOpen(true); setAuthOk(false); setToken('');
        window.localStorage.removeItem('jwt'); window.localStorage.setItem('userEmail', registerEmail);
        setAuthModalOpen(false); return;
      }

      if (t) { setToken(t); setAuthOk(true); window.localStorage.setItem('jwt', t); window.localStorage.setItem('userEmail', registerEmail); }
      else { setAuthOk(false); setToken(''); }
      scrollToPricing();
      if (pendingCheckoutPlan) {
        const plan = pendingCheckoutPlan; setPendingCheckoutPlan(null);
        await handleCheckout(plan, true, registerEmail);
      } else { setPlanDialogOpen(true); }
      setAuthModalOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      const errorText = data?.error || '';
      if (status === 409 || data?.account_exists || /already exists/i.test(errorText)) {
        setAuthTab('signin'); setEmail(registerEmail); setLoginPassword('');
        setAuthError('Account already exists. Please sign in or reset your password.'); return;
      }
      setAuthError(errorText || 'Sign up failed. Try again with a different email.');
    }
  };

  const handleForgotPassword = async () => {
    setAuthError(''); setForgotPasswordLoading(true);
    const emailToReset = forgotPasswordEmail.trim().toLowerCase();
    if (!isValidEmail(emailToReset)) {
      setAuthError('Please enter a valid email address.'); setForgotPasswordLoading(false); return;
    }
    try {
      // Tell the backend which site this request came from, so the emailed
      // link lands where this person actually uses the product. Embedded on a
      // partner's page that is their origin; on our own site it is ours. The
      // backend only uses it to pick between URLs it already has configured —
      // it never emails this value — so a forged origin cannot redirect
      // somebody else's reset link.
      const parentUrl = getEmbedParentUrl();
      let origin = window.location.origin;
      try { if (parentUrl) origin = new URL(parentUrl).origin; } catch (_) {}
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email: emailToReset, origin });
      setForgotPasswordSent(true); setAuthError('');
    } catch (err) {
      if (err?.response?.status === 429) setAuthError('Too many requests. Please wait an hour before trying again.');
      else setForgotPasswordSent(true); // Don't reveal if email exists
    } finally { setForgotPasswordLoading(false); }
  };

  const handleResetPassword = async () => {
    setAuthError(''); setFormErrors({}); setResetPasswordLoading(true);
    if (!newPassword) { setFormErrors({ newPassword: 'Password is required' }); setResetPasswordLoading(false); return; }
    if (newPassword.length < 6) { setFormErrors({ newPassword: 'Password must be at least 6 characters' }); setResetPasswordLoading(false); return; }
    if (newPassword !== confirmNewPassword) { setAuthError('Passwords do not match.'); setResetPasswordLoading(false); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/reset-password`, { token: resetPasswordToken, password: newPassword });
      const t = res?.data?.token;
      if (t) {
        setToken(t); setAuthOk(true); window.localStorage.setItem('jwt', t);
        if (res?.data?.email) { window.localStorage.setItem('userEmail', res.data.email); setEmail(res.data.email); }
        setSubscriptionRequired(Boolean(res?.data?.subscription_required));
        setSubscriptionStatus(res?.data?.subscription_status || 'active');
      }
      setResetPasswordOpen(false); setResetPasswordToken(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (err) {
      setAuthError(err?.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally { setResetPasswordLoading(false); }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true); setAuthError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/resend-verification`, { email: verificationEmail });
      setVerificationSent(true);
    } catch (err) {
      setAuthError(err?.response?.data?.error || 'Failed to resend verification email.');
    } finally { setResendingVerification(false); }
  };

  const handlePlanDialogSelect = (planId) => {
    setPlanDialogOpen(false);
    if (planId === 'free') { return; }
    handleCheckout(planId, true, registerEmail);
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false); setForgotPasswordEmail('');
    setForgotPasswordSent(false); setAuthError('');
  };

  const closeVerificationDialog = () => {
    setVerificationDialogOpen(false); setVerificationSent(false); setAuthError('');
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* ═══════════════════ Auth modal (Sign in / Sign up tabs) ═══════════════════ */}
      <Dialog open={authModalOpen} onClose={() => setAuthModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Account</DialogTitle>
        <DialogContent>
          <Tabs
            value={authTab}
            onChange={(_, val) => { setAuthTab(val); setAuthError(''); setFormErrors({}); }}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab label="Sign In" value="signin" />
            <Tab label="Create Account" value="signup" />
          </Tabs>

          {authTab === 'signin' && (
            <Box component="form" noValidate autoComplete="off">
              <TextField fullWidth margin="normal" label="Email" placeholder="you@example.com" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                error={Boolean(formErrors.email)} helperText={formErrors.email || ''} />
              <TextField fullWidth margin="normal" label="Password" placeholder="••••••••"
                type={showLoginPassword ? 'text' : 'password'}
                value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                error={Boolean(formErrors.loginPassword)} helperText={formErrors.loginPassword || ''}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="Toggle password visibility" onClick={() => setShowLoginPassword(v => !v)} edge="end">
                      {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}} />
              <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={handleLogin}>Sign In</Button>
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Button variant="text" size="small" onClick={() => { setForgotPasswordEmail(email || ''); setForgotPasswordSent(false); setAuthError(''); setForgotPasswordOpen(true); }}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}>
                  Forgot your password?
                </Button>
              </Box>
            </Box>
          )}

          {authTab === 'signup' && (
            <Box component="form" noValidate autoComplete="off">
              <TextField fullWidth margin="normal" label="Email" placeholder="you@example.com" type="email"
                value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)}
                error={Boolean(formErrors.registerEmail)} helperText={formErrors.registerEmail || ''} />
              <TextField fullWidth margin="normal" label="Password" placeholder="Create a password"
                type={showRegisterPassword ? 'text' : 'password'}
                value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)}
                error={Boolean(formErrors.registerPassword)} helperText={formErrors.registerPassword || ''}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="Toggle password visibility" onClick={() => setShowRegisterPassword(v => !v)} edge="end">
                      {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}} />
              <TextField fullWidth margin="normal" label="Confirm Password" placeholder="Repeat password"
                type={showRegisterPassword ? 'text' : 'password'}
                value={registerConfirm} onChange={(e) => setRegisterConfirm(e.target.value)} />
              <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={handleRegister}>Create Account</Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }}>or</Divider>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 50 }}>
            {GOOGLE_CLIENT_ID ? <div ref={googleButtonRef} /> : (
              <Button variant="outlined" fullWidth disabled>Google sign-in unavailable (set REACT_APP_GOOGLE_CLIENT_ID)</Button>
            )}
          </Box>
          {!googleAvailable && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              If the Google button does not appear, try allowing popups/cookies or continue in a standard window.
            </Typography>
          )}
          {authError && <Alert severity="error" sx={{ mt: 2 }}>{authError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════ Plan Selection Dialog (post-signup) ═══════════════════ */}
      <Dialog open={planDialogOpen} onClose={(_, reason) => { if (reason !== 'backdropClick') setPlanDialogOpen(false); }}
        maxWidth="md" fullWidth disableEscapeKeyDown>
        <DialogTitle>Choose your plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a plan to activate {registerEmail || email || 'your account'}.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(220px, 1fr))' }, gap: 2.5 }}>
            {planCards.map((plan) => (
              <PlanCard key={plan.id} plan={plan} billingPeriod="monthly"
                onSelect={handlePlanDialogSelect} ctaOverride={plan.modalCta} />
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════ Forgot Password Dialog ═══════════════════ */}
      <Dialog open={forgotPasswordOpen} onClose={closeForgotPassword} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Your Password</DialogTitle>
        <DialogContent>
          {!forgotPasswordSent ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the email associated with your account. If it exists, we'll send a password reset link.
              </Typography>
              <TextField fullWidth label="Email" type="email" value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                disabled={forgotPasswordLoading} margin="normal" />
              {authError && <Alert severity="error" sx={{ mt: 1 }}>{authError}</Alert>}
            </>
          ) : (
            <Alert severity="success">
              If an account exists for <strong>{forgotPasswordEmail}</strong>, a reset link has been sent.
              Check your inbox (and spam folder). The link expires in 1 hour.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForgotPassword} disabled={forgotPasswordLoading}>Close</Button>
          {!forgotPasswordSent && (
            <Button variant="contained" onClick={handleForgotPassword}
              disabled={forgotPasswordLoading || !forgotPasswordEmail}>
              {forgotPasswordLoading ? <CircularProgress size={20} /> : 'Send Reset Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════════════ Reset Password Dialog (token from URL) ═══════════════════ */}
      <Dialog open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Choose a New Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter and confirm your new password. It must be at least 6 characters.
          </Typography>
          <TextField fullWidth margin="normal" label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            error={Boolean(formErrors.newPassword)} helperText={formErrors.newPassword || ''}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword(v => !v)} edge="end">
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )}} />
          <TextField fullWidth margin="normal" label="Confirm New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
          {authError && <Alert severity="error" sx={{ mt: 1 }}>{authError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordOpen(false)} disabled={resetPasswordLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={resetPasswordLoading}>
            {resetPasswordLoading ? <CircularProgress size={20} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════ Email Verification — "check your inbox" prompt ═══════════════════ */}
      <Dialog open={verificationDialogOpen} onClose={closeVerificationDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Your Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A verification email was sent to <strong>{verificationEmail}</strong>.
            Please check your inbox (and spam folder) and click the link to activate your account.
          </Typography>
          {verificationSent && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Verification email sent! Please <strong>check your spam folder</strong> just in case.
            </Alert>
          )}
          {authError && <Alert severity="error" sx={{ mt: 2 }}>{authError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVerificationDialog}>Close</Button>
          <Button variant="contained" onClick={handleResendVerification} disabled={resendingVerification}>
            {resendingVerification ? <CircularProgress size={20} /> : (verificationSent ? 'Resend Again' : 'Resend Verification Email')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Email Verification Result (after clicking link in email) ─── */}
      <Dialog open={verifyEmailResult !== null} onClose={() => setVerifyEmailResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{verifyEmailResult?.success ? 'Email Verified!' : 'Verification Failed'}</DialogTitle>
        <DialogContent>
          <Alert severity={verifyEmailResult?.success ? 'success' : 'error'}>{verifyEmailResult?.message}</Alert>
          {verifyEmailResult?.success && <Typography variant="body2" sx={{ mt: 2 }}>You can now sign in to your account.</Typography>}
        </DialogContent>
        <DialogActions>
          {verifyEmailResult?.success ? (
            <Button variant="contained" onClick={() => { setVerifyEmailResult(null); setAuthModalOpen(true); setAuthTab('signin'); }}>Sign In</Button>
          ) : (
            <Button onClick={() => setVerifyEmailResult(null)}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ─── Email Verifying Loading State ─── */}
      <Dialog open={verifyingEmail} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Verifying your email...</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
